import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Inbox, Loader2, CheckCircle, XCircle, ShieldAlert, Mail, KeyRound, AlertTriangle, RefreshCw, Save,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type Overview = {
  seats: {
    totalCap: number;
    baselineConsumed: number;
    agencyReserve: number;
    trackedConsumed: number;
    totalConsumed: number;
    tenantBudget: number;
    remainingOfCap: number;
    remainingOfTenantBudget: number;
  };
  funnel: {
    requestsPending: number;
    requestsApproved: number;
    requestsRejected: number;
    grantsGranted: number;
    grantsConsumed: number;
    grantsRevoked: number;
  };
  neverbounce: { liveChecksTotal: number };
  requestPageViews: number;
};

type AccessRequest = {
  id: string; workspaceId: string; userId: string; userEmail: string;
  reason: string | null; status: "pending" | "approved" | "rejected";
  decisionNote: string | null; appealText: string | null; appealedAt: string | null;
  reviewedAt: string | null; createdAt: string; updatedAt: string;
};

type Grant = {
  id: string; workspaceId: string; userId: string; userEmail: string;
  status: "granted" | "consumed" | "revoked";
  grantedAt: string; oauthCompletedAt: string | null; revokedAt: string | null;
};

type NotifyInterest = {
  id: string; workspaceId: string; userId: string; userEmail: string; createdAt: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const REQUEST_BADGE: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};
const GRANT_BADGE: Record<string, string> = {
  granted: "bg-blue-100 text-blue-700",
  consumed: "bg-green-100 text-green-700",
  revoked: "bg-slate-200 text-slate-600",
};

function Stat({ label, value, hint, tone }: { label: string; value: React.ReactNode; hint?: string; tone?: "warn" | "danger" }) {
  return (
    <div className="rounded-lg border border-border p-3" data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold mt-0.5 ${tone === "danger" ? "text-red-600" : tone === "warn" ? "text-amber-600" : ""}`}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function GmailReadContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<"pending" | "approved" | "rejected" | "all">("pending");
  const [decisionTarget, setDecisionTarget] = useState<{ req: AccessRequest; mode: "approve" | "reject" } | null>(null);
  const [decisionNote, setDecisionNote] = useState("");
  const [overrideCap, setOverrideCap] = useState(false);
  const [hardDeleteTarget, setHardDeleteTarget] = useState<Grant | null>(null);

  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewError,
    refetch: refetchOverview,
  } = useQuery<Overview>({
    queryKey: ["/api/platform/gmail-read/overview"],
  });

  // Step 6 — editable manual baseline seat counter.
  const { data: baselineResp } = useQuery<{ baselineConsumed: number; default: number }>({
    queryKey: ["/api/platform/gmail-read/baseline"],
  });
  const [baselineInput, setBaselineInput] = useState<string>("");
  const baselineEffective = baselineResp?.baselineConsumed;
  const baselineDirty = baselineInput !== "" && baselineInput !== String(baselineEffective ?? "");

  const { data: requestsResp, isLoading: requestsLoading } = useQuery<{ requests: AccessRequest[] }>({
    queryKey: ["/api/platform/gmail-read/requests", statusFilter],
    queryFn: () =>
      fetch(`/api/platform/gmail-read/requests?status=${statusFilter}`, { credentials: "include" }).then((r) => r.json()),
  });
  const requests = requestsResp?.requests ?? [];

  const { data: grantsResp } = useQuery<{ grants: Grant[] }>({
    queryKey: ["/api/platform/gmail-read/grants"],
    queryFn: () => fetch(`/api/platform/gmail-read/grants`, { credentials: "include" }).then((r) => r.json()),
  });
  const grants = grantsResp?.grants ?? [];

  // Task #409 — "Notify me when it's live" demand signal (passive tracked list).
  const { data: notifyResp } = useQuery<{ interest: NotifyInterest[]; count: number }>({
    queryKey: ["/api/platform/gmail-read/notify-interest"],
    queryFn: () => fetch(`/api/platform/gmail-read/notify-interest`, { credentials: "include" }).then((r) => r.json()),
  });
  const notifyInterest = notifyResp?.interest ?? [];

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail-read/overview"] });
    queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail-read/requests"] });
    queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail-read/grants"] });
  };

  const approveMutation = useMutation({
    mutationFn: async (args: { id: string; decisionNote: string; overrideCap: boolean }) => {
      const response = await apiRequest("POST", `/api/platform/gmail-read/requests/${args.id}/approve`, {
        decisionNote: args.decisionNote,
        overrideCap: args.overrideCap,
      });
      return response.json();
    },
    onSuccess: () => {
      invalidateAll();
      setDecisionTarget(null);
      setDecisionNote("");
      setOverrideCap(false);
      toast({ title: "Approved", description: "Seat grant allocated — the user can now connect read access." });
    },
    onError: (error: any) => {
      // Seat-cap guard returns a 409 with requiresOverride.
      if (error?.requiresOverride || /budget exhausted/i.test(error?.message || "")) {
        toast({
          title: "Seat budget exhausted",
          description: "Approving would risk Google's lifetime cap. Re-confirm with override to proceed.",
          variant: "destructive",
        });
        setOverrideCap(true);
        return;
      }
      toast({ title: "Error", description: error?.message || "Failed to approve", variant: "destructive" });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (args: { id: string; decisionNote: string }) => {
      const response = await apiRequest("POST", `/api/platform/gmail-read/requests/${args.id}/reject`, {
        decisionNote: args.decisionNote,
      });
      return response.json();
    },
    onSuccess: () => {
      invalidateAll();
      setDecisionTarget(null);
      setDecisionNote("");
      toast({ title: "Rejected", description: "The requester can appeal the decision." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message || "Failed to reject", variant: "destructive" });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/platform/gmail-read/grants/${id}/revoke`, {});
      return response.json();
    },
    onSuccess: () => {
      invalidateAll();
      toast({
        title: "Grant revoked",
        description: "Access removed. Note: Google never refunds a consumed lifetime slot.",
      });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message || "Failed to revoke", variant: "destructive" });
    },
  });

  const hardDeleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/platform/gmail-read/grants/${id}/hard-delete`, {});
      return response.json();
    },
    onSuccess: (data: any) => {
      invalidateAll();
      setHardDeleteTarget(null);
      toast({
        title: "Reset complete",
        description: `Grant deleted. ${data.requestsDeleted} request(s) also removed. The user can now submit a fresh request.`,
      });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message || "Failed to reset grant", variant: "destructive" });
    },
  });

  const baselineMutation = useMutation({
    mutationFn: async (value: number) => {
      const response = await apiRequest("PUT", `/api/platform/gmail-read/baseline`, { baselineConsumed: value });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail-read/baseline"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail-read/overview"] });
      setBaselineInput("");
      toast({ title: "Baseline updated", description: "Seat consumption recalculated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message || "Failed to update baseline", variant: "destructive" });
    },
  });

  const seats = overview?.seats;
  const seatTone: "warn" | "danger" | undefined =
    seats && seats.remainingOfTenantBudget <= 0 ? "danger" : seats && seats.remainingOfTenantBudget <= 5 ? "warn" : undefined;

  return (
    <>
      <div className="space-y-6" data-testid="page-platform-gmail-read">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Inbox className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Gmail Read Access</h1>
            <p className="text-sm text-muted-foreground">
              Restricted <code className="text-xs">gmail.readonly</code> scope — capped by Google's 100-user lifetime limit. Approve sparingly.
            </p>
          </div>
        </div>

        {/* Seat / usage overview (T006) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="h-4 w-4" /> Seat consumption
            </CardTitle>
            <CardDescription>
              Consumed slots are permanent — revoking access does not return a slot to the pool.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading seat data…
              </div>
            ) : overviewError || !seats ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="state-overview-error">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
                <div className="text-sm text-muted-foreground">
                  Couldn't load seat data. The service may be temporarily unavailable.
                </div>
                <Button variant="outline" size="sm" onClick={() => refetchOverview()} data-testid="button-retry-overview">
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat
                  label="Consumed"
                  value={`${seats.totalConsumed} / ${seats.totalCap}`}
                  hint={`${seats.baselineConsumed} baseline + ${seats.trackedConsumed} tracked`}
                  tone={seatTone}
                />
                <Stat
                  label="Tenant budget left"
                  value={seats.remainingOfTenantBudget}
                  hint={`of ${seats.tenantBudget} (cap − ${seats.agencyReserve} reserve)`}
                  tone={seatTone}
                />
                <Stat label="Reserve (agency)" value={seats.agencyReserve} hint="held back for in-house agents" />
                <Stat label="Cap remaining" value={seats.remainingOfCap} hint="hard Google lifetime headroom" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Editable manual baseline (step 6) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="h-4 w-4" /> Baseline seat counter
            </CardTitle>
            <CardDescription>
              Read slots consumed before per-grant tracking existed (internal/agency mailboxes). Total consumed = this baseline + tracked grants.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="baseline-input" className="text-xs text-muted-foreground">Baseline consumed</Label>
                <Input
                  id="baseline-input"
                  type="number"
                  min={0}
                  max={100}
                  className="w-32"
                  value={baselineInput !== "" ? baselineInput : (baselineEffective ?? "")}
                  onChange={(e) => setBaselineInput(e.target.value)}
                  data-testid="input-baseline-consumed"
                />
              </div>
              <Button
                size="sm"
                disabled={!baselineDirty || baselineMutation.isPending}
                onClick={() => {
                  const n = parseInt(baselineInput, 10);
                  if (Number.isFinite(n)) baselineMutation.mutate(n);
                }}
                data-testid="button-save-baseline"
              >
                {baselineMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                Save
              </Button>
              {baselineResp && (
                <span className="text-[11px] text-muted-foreground">Default: {baselineResp.default}</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Funnel + usage counters */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Pending requests" value={overview.funnel.requestsPending} tone={overview.funnel.requestsPending > 0 ? "warn" : undefined} />
            <Stat label="Active grants" value={overview.funnel.grantsGranted + overview.funnel.grantsConsumed} hint={`${overview.funnel.grantsConsumed} connected`} />
            <Stat label="NeverBounce live checks" value={overview.neverbounce.liveChecksTotal} hint="lifetime billed verifications" />
            <Stat label="Notify-me signups" value={notifyResp?.count ?? notifyInterest.length} hint="asked to be told when live" />
            <Stat label="Request page views" value={overview.requestPageViews} hint="interest signal" />
          </div>
        )}

        {/* Review queue (T005) */}
        <Card>
          <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldAlert className="h-4 w-4" /> Access requests
              </CardTitle>
              <CardDescription>Approve to mint a per-user seat grant. Rejections can be appealed.</CardDescription>
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
              <SelectTrigger className="w-36" data-testid="select-request-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {requestsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading requests…
              </div>
            ) : requests.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="text-no-requests">
                No {statusFilter !== "all" ? statusFilter : ""} requests.
              </div>
            ) : (
              <div className="space-y-2">
                {requests.map((req) => (
                  <div
                    key={req.id}
                    className="flex flex-col md:flex-row md:items-center gap-3 rounded-lg border border-border p-3"
                    data-testid={`row-request-${req.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate" data-testid={`text-request-email-${req.id}`}>{req.userEmail}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          ws: {req.workspaceId}{req.reason ? ` · "${req.reason}"` : ""}
                        </div>
                        {req.appealText !== null && req.appealedAt && (
                          <div className="text-[11px] text-amber-600 mt-0.5">Appealed{req.appealText ? `: ${req.appealText}` : ""}</div>
                        )}
                        {req.status === "rejected" && req.decisionNote && (
                          <div className="text-[11px] text-red-600 mt-0.5">Note: {req.decisionNote}</div>
                        )}
                      </div>
                    </div>
                    <Badge className={REQUEST_BADGE[req.status]} data-testid={`badge-request-status-${req.id}`}>{req.status}</Badge>
                    {req.status !== "approved" && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="h-8"
                          onClick={() => { setDecisionTarget({ req, mode: "approve" }); setDecisionNote(""); setOverrideCap(false); }}
                          data-testid={`button-approve-${req.id}`}
                        >
                          <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          onClick={() => { setDecisionTarget({ req, mode: "reject" }); setDecisionNote(""); }}
                          data-testid={`button-reject-${req.id}`}
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Grants list */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Seat grants</CardTitle>
            <CardDescription>Per-user grants. A grant becomes a permanent consumed slot once the user connects.</CardDescription>
          </CardHeader>
          <CardContent>
            {grants.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="text-no-grants">No grants yet.</div>
            ) : (
              <div className="space-y-2">
                {grants.map((g) => (
                  <div key={g.id} className="flex items-center gap-3 rounded-lg border border-border p-3" data-testid={`row-grant-${g.id}`}>
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{g.userEmail}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        ws: {g.workspaceId}{g.oauthCompletedAt ? " · slot consumed" : " · awaiting connect"}
                      </div>
                    </div>
                    <Badge className={GRANT_BADGE[g.status]}>{g.status}</Badge>
                    {g.status !== "revoked" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={() => revokeMutation.mutate(g.id)}
                        disabled={revokeMutation.isPending}
                        data-testid={`button-revoke-${g.id}`}
                      >
                        Revoke
                      </Button>
                    )}
                    {g.status === "revoked" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        onClick={() => setHardDeleteTarget(g)}
                        data-testid={`button-reset-grant-${g.id}`}
                      >
                        Reset for demo
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* "Notify me when it's live" interest (Task #409) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Notify-me list</CardTitle>
            <CardDescription>
              Users who asked to be told when in-app inbox reading ships. No email is sent automatically — reach out manually.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {notifyInterest.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="text-no-notify-interest">
                No notify-me signups yet.
              </div>
            ) : (
              <div className="space-y-2">
                {notifyInterest.map((n) => (
                  <div key={n.id} className="flex items-center gap-3 rounded-lg border border-border p-3" data-testid={`row-notify-${n.id}`}>
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate" data-testid={`text-notify-email-${n.id}`}>{n.userEmail}</div>
                      <div className="text-xs text-muted-foreground truncate">ws: {n.workspaceId}</div>
                    </div>
                    <div className="text-xs text-muted-foreground shrink-0" data-testid={`text-notify-date-${n.id}`}>
                      {new Date(n.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Decision dialog */}
      <Dialog open={!!decisionTarget} onOpenChange={(open) => { if (!open) { setDecisionTarget(null); setOverrideCap(false); } }}>
        <DialogContent data-testid="dialog-decision">
          <DialogHeader>
            <DialogTitle>
              {decisionTarget?.mode === "approve" ? "Approve read access" : "Reject read access"}
            </DialogTitle>
            <DialogDescription>
              {decisionTarget?.req.userEmail}
            </DialogDescription>
          </DialogHeader>

          {decisionTarget?.mode === "approve" && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Approving allocates one of a strictly limited number of Google restricted-scope slots.
                Once the user connects, the slot is permanently spent and is not refunded on revoke.
              </span>
            </div>
          )}

          <Textarea
            placeholder={decisionTarget?.mode === "approve" ? "Optional note for the requester…" : "Reason for rejection (shown to requester)…"}
            value={decisionNote}
            onChange={(e) => setDecisionNote(e.target.value)}
            data-testid="textarea-decision-note"
          />

          {overrideCap && decisionTarget?.mode === "approve" && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-800 flex gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Seat budget is exhausted. Confirming will override the cap guard — only do this if you understand the risk.</span>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDecisionTarget(null); setOverrideCap(false); }} data-testid="button-cancel-decision">
              Cancel
            </Button>
            {decisionTarget?.mode === "approve" ? (
              <Button
                onClick={() => decisionTarget && approveMutation.mutate({ id: decisionTarget.req.id, decisionNote, overrideCap })}
                disabled={approveMutation.isPending}
                data-testid="button-confirm-approve"
              >
                {approveMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                {overrideCap ? "Override & approve" : "Approve"}
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() => decisionTarget && rejectMutation.mutate({ id: decisionTarget.req.id, decisionNote })}
                disabled={rejectMutation.isPending}
                data-testid="button-confirm-reject"
              >
                {rejectMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                Reject
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hard-delete / demo reset confirmation dialog */}
      <Dialog open={!!hardDeleteTarget} onOpenChange={(open) => { if (!open) setHardDeleteTarget(null); }}>
        <DialogContent data-testid="dialog-hard-delete">
          <DialogHeader>
            <DialogTitle>Reset for demo?</DialogTitle>
            <DialogDescription>
              This will permanently delete the grant and all access requests for{" "}
              <span className="font-medium">{hardDeleteTarget?.userEmail}</span> in this workspace.
              The user will be able to submit a fresh request and go through the full approval flow again.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              If the user previously completed Google OAuth, the Google lifetime slot is already spent and cannot be recovered.
              This reset only clears the database records — it does not restore any Google quota.
            </span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHardDeleteTarget(null)} data-testid="button-cancel-hard-delete">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => hardDeleteTarget && hardDeleteMutation.mutate(hardDeleteTarget.id)}
              disabled={hardDeleteMutation.isPending}
              data-testid="button-confirm-hard-delete"
            >
              {hardDeleteMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
              Yes, reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function PlatformGmailReadPage() {
  return (
    <PlatformLayout>
      <GmailReadContent />
    </PlatformLayout>
  );
}
